import React, { useMemo, useState, useRef } from 'react';
import { Typography } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridColumnGroupingModel,
} from '@mui/x-data-grid';
import { MuiButton, MuiDrawer } from '@ace/ace-ui-web';
import pagesStandardStyles from '../../../Pages.module.css';
import localLiftMaxStyles from './LocalLiftMax.module.css';
import { getDataGridStyles } from '../../../GridStyles';
import { getGroupColumnHeaderStyles } from '../../../GroupColumnHeaderGridStyles';
import {
  formatNumberWithCommas,
  formatLoadDate,
  isValidApiDate,
  isPercentageFixedColumn,
  roundToOneDecimal,
  roundToWholeNumber,
  isCurrencyFixedColumn,
  isMobileDevice,
  renderEmptyValue,
  getAlternatingRowClassName,
} from '../../../../utils/appUtils';
import {
  defaultPerformanceLLMGrid,
  PerformanceLLMGrid,
  ROASGuaranteeRequirements,
  ROASGuaranteeRequirementsMultiStore,
} from '../../../../models/PerformanceLLMGridModel';
import { kpiContentMap } from '../../../../utils/viewInfoUtils';
import { useBuildDynamicUrl } from '../../../../hooks/useDynamicUrl';
import { useClickHandler } from '../../../../utils/usageMetricsLogger';
import lang from '../../../../languages/en.json';
import SkeletonLocalLiftMax from '../../../../components/skeletons/performance/localliftmax';
import { useAppSelector } from '../../../../redux/hooks';
import {
  selectLocalLiftMaxData,
  selectLocalLiftMaxLoading,
} from '../../../../redux/PerformanceReducer/LocalLift/localLiftMaxSlice';

const PAGE_NM = 'Performance';
const TAB_NM = 'Local Lift Max';

// ─── Props interface ─────────────────────────────────────────────────────────

interface LocalLiftMaxProps {
  /** Pre-fetched data for print view — skips Redux read and API fetch when provided */
  data?: PerformanceLLMGrid | null;
  /** When true, hides interactive elements (buttons, drawer, ROAS table) */
  isPrintView?: boolean;
}

// Define currency keywords specific to this grid
const CURRENCY_KEYWORDS = ['revenue', 'spend', '$'];

// Type guard: detects multi-store ROAS shape by key presence (values can be null)
const isMultiStoreRoasRequirements = (
  value: ROASGuaranteeRequirements | ROASGuaranteeRequirementsMultiStore,
): value is ROASGuaranteeRequirementsMultiStore =>
  'multiStoreEcommerce' in value &&
  'multiStoreInStorePickUp' in value &&
  'multiStoreGoogleBusinessProfile' in value;

// Builds ROAS rows for multi-store mode (numeric store counts)
const buildMultiStoreRoasRows = (roasReqs: ROASGuaranteeRequirementsMultiStore) => [
  {
    id: 1,
    requirement: lang.localLiftMaxContent.roasEcommerce,
    value: `${roasReqs.multiStoreEcommerce ?? 0}`,
  },
  {
    id: 2,
    requirement: lang.localLiftMaxContent.roasInStorePickUp,
    value: `${roasReqs.multiStoreInStorePickUp ?? 0}`,
  },
  {
    id: 3,
    requirement: lang.localLiftMaxContent.roasGoogleBusinessProfile,
    value: `${roasReqs.multiStoreGoogleBusinessProfile ?? 0}`,
  },
];

// Converts a nullable boolean ROAS flag to display value: null → '-', true → 'YES', false → 'NO'
const formatRoasFlag = (flag: boolean | null): string =>
  flag == null ? '-' : flag ? lang.pageContent.yes : lang.pageContent.no;

// Builds ROAS rows for single-store mode (YES/NO/- per requirement)
const buildSingleStoreRoasRows = (roasReqs: ROASGuaranteeRequirements) => [
  {
    id: 1,
    requirement: lang.localLiftMaxContent.roasEcommerce,
    value: formatRoasFlag(roasReqs.isEcommerce),
  },
  {
    id: 2,
    requirement: lang.localLiftMaxContent.roasInStorePickUp,
    value: formatRoasFlag(roasReqs.isInStorePickUp),
  },
  {
    id: 3,
    requirement: lang.localLiftMaxContent.roasGoogleBusinessProfile,
    value: formatRoasFlag(roasReqs.isGoogleBusinessProfile),
  },
];

// Row interface for DataGrid
interface GridRow {
  id: number;
  fixedColumn: string;
  dataKey: 'thisYear' | 'lastYear' | 'yearOverYear';
}

// Helper function to format cells with currency/numbers
const renderFormatCell = (
  params: GridRenderCellParams<GridRow, number | null>,
): JSX.Element | string => {
  const emptyCheck = renderEmptyValue(params.value);
  if (emptyCheck) return emptyCheck;

  const columnHeader = params.colDef.headerName || '';
  const fixedColumnLabel = params.row.fixedColumn;

  // Check if it's Year Over Year row and format as percentage (check this FIRST)
  // Round to 1 decimal place per spec
  if (params.row.dataKey === 'yearOverYear') {
    return `${roundToOneDecimal(params.value)}%`;
  }

  // OMNI Return Ad Spend always displays 2 decimal places (e.g. $7,587.60)
  if (columnHeader === lang.localLiftMaxContent.columnOmniReturnAdSpend) {
    return `$${formatNumberWithCommas(params.value, true, 2)}`;
  }

  // Add $ for currency-based column headers — round to whole number (e.g. $96,411)
  if (isCurrencyFixedColumn(columnHeader, CURRENCY_KEYWORDS)) {
    return `$${formatNumberWithCommas(roundToWholeNumber(params.value))}`;
  }

  // Add % for percentage-based fixed column labels
  // Round to one decimal place for percentage values
  if (isPercentageFixedColumn(fixedColumnLabel)) {
    return `${roundToOneDecimal(params.value)}%`;
  }

  // Default: round to whole number and format with commas (e.g. store visit counts)
  return formatNumberWithCommas(roundToWholeNumber(params.value));
};

// Helper function to create column group
function createColumnGroup(
  groupName: 'latestMonthly' | 'yearToDate',
  mockData: PerformanceLLMGrid,
): GridColDef[] {
  const columnConfig = {
    minWidth: 130.2,
    flex: 1,
  };

  const columns: GridColDef[] = [
    {
      field: `${groupName}StoreVisits`,
      headerName: lang.localLiftMaxContent.columnStoreVisits,
      ...columnConfig,
      headerClassName: pagesStandardStyles.columnHeader,
      align: 'right',
      valueGetter: (_, gridRow) => mockData[groupName].storeVisits[gridRow.dataKey],
      renderCell: renderFormatCell,
    },
    {
      field: `${groupName}OmniRevenue`,
      headerName: lang.localLiftMaxContent.columnOmniRevenue,
      ...columnConfig,
      headerClassName: pagesStandardStyles.columnHeader,
      align: 'right',
      valueGetter: (_, gridRow) => mockData[groupName].omniRevenue[gridRow.dataKey],
      renderCell: renderFormatCell,
    },
  ];

  // Year-to-Date has additional columns
  if (groupName === 'yearToDate') {
    columns.push(
      {
        field: `${groupName}Spend`,
        headerName: lang.localLiftMaxContent.columnSpend,
        ...columnConfig,
        headerClassName: pagesStandardStyles.columnHeader,
        align: 'right',
        valueGetter: (_, gridRow) => mockData[groupName].spend[gridRow.dataKey],
        renderCell: renderFormatCell,
      },
      {
        field: `${groupName}OmniReturnAdSpend`,
        headerName: lang.localLiftMaxContent.columnOmniReturnAdSpend,
        ...columnConfig,
        headerClassName: pagesStandardStyles.columnHeader,
        align: 'right',
        valueGetter: (_, gridRow) => mockData[groupName].omniReturnAdSpend[gridRow.dataKey],
        renderCell: renderFormatCell,
      },
    );
  }

  return columns;
}

const LocalLiftMax: React.FC<LocalLiftMaxProps> = ({ data: dataProp, isPrintView = false }) => {
  // Data fetch handled by Header
  const buildDynamicUrl = useBuildDynamicUrl();
  const handleAndLogClick = useClickHandler(PAGE_NM, TAB_NM);
  const reduxData = useAppSelector(selectLocalLiftMaxData);
  const reduxLoading = useAppSelector(selectLocalLiftMaxLoading);

  // Use prop data (print view) or Redux data (live view)
  const localLiftMaxData = dataProp ?? reduxData;
  const isLoading = isPrintView ? false : reduxLoading;
  // Track if we've received the first response to prevent flashing error on initial mount
  const hasLoadedRef = useRef<boolean>(false);
  if (!isLoading && !hasLoadedRef.current) {
    hasLoadedRef.current = true;
  }

  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [drawerContent, setDrawerContent] = useState<{
    title: string;
    content: React.ReactNode;
  }>({
    title: '',
    content: null,
  });

  // Handler for View Monthly Performance button
  const handleViewMonthlyPerformance = (): void => {
    const url = buildDynamicUrl('WFC', 'LLMMPD');
    if (url) {
      handleAndLogClick('Local Lift Max - View Monthly Performance', { url, urlTarget: '_blank' });
    }
  };

  // Create rows for the grid
  const rowData = useMemo(
    () => [
      { id: 1, fixedColumn: lang.localLiftMaxContent.rowThisYear, dataKey: 'thisYear' as const },
      { id: 2, fixedColumn: lang.localLiftMaxContent.rowLastYear, dataKey: 'lastYear' as const },
      {
        id: 3,
        fixedColumn: lang.localLiftMaxContent.rowYearOverYear,
        dataKey: 'yearOverYear' as const,
      },
    ],
    [],
  );

  // Create column groups
  const latestMonthlyColumns = useMemo(
    () => createColumnGroup('latestMonthly', localLiftMaxData || defaultPerformanceLLMGrid),
    [localLiftMaxData],
  );
  const yearToDateColumns = useMemo(
    () => createColumnGroup('yearToDate', localLiftMaxData || defaultPerformanceLLMGrid),
    [localLiftMaxData],
  );

  // Define all columns
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'fixedColumn',
        headerName: lang.localLiftMaxContent.columnPeriod,
        width: 180,
        headerClassName: pagesStandardStyles.columnHeader,
        cellClassName: pagesStandardStyles.stickyColumn,
      },
      // Latest Monthly group
      ...latestMonthlyColumns,
      // Year-to-Date group
      ...yearToDateColumns,
    ],
    [latestMonthlyColumns, yearToDateColumns],
  );

  // Define column grouping model
  const columnGroupingModel: GridColumnGroupingModel = useMemo(() => {
    const data = localLiftMaxData || defaultPerformanceLLMGrid;
    const latestMonthlyFields = latestMonthlyColumns.map(col => col.field);
    const yearToDateFields = yearToDateColumns.map(col => col.field);

    return [
      {
        groupId: 'latest-monthly-group',
        headerName: `${lang.localLiftMaxContent.latestMonthlyPrefix} ${isValidApiDate(data.latestMonthlyDate) ? formatLoadDate(data.latestMonthlyDate, false, -2, 'long') : 'N/A'}`,
        children: latestMonthlyFields.map(field => ({ field })),
      },
      {
        groupId: 'year-to-date-group',
        headerName: `${lang.localLiftMaxContent.yearToDatePrefix} ${isValidApiDate(data.yearToDateThruDate) ? formatLoadDate(data.yearToDateThruDate, false, -2, 'long') : 'N/A'}`,
        children: yearToDateFields.map(field => ({ field })),
      },
    ];
  }, [latestMonthlyColumns, yearToDateColumns, localLiftMaxData]);

  // Get the last field from the first column group for styling
  const lastGroupOneField = useMemo(() => {
    return latestMonthlyColumns.length > 0
      ? latestMonthlyColumns[latestMonthlyColumns.length - 1].field
      : '';
  }, [latestMonthlyColumns]);

  // Create rows for ROAS table
  // ROAS Guarantee Requirements display different data formats based on mode:
  // - Multi-store mode: Shows NUMERIC COUNTS of stores meeting ROAS requirements (e.g., "14" stores)
  // - Single-store mode: Shows YES/NO/"-" values indicating if this store meets requirements
  const roasRowData = useMemo(() => {
    const roasReqs = (localLiftMaxData || defaultPerformanceLLMGrid).roasRequirements;
    return isMultiStoreRoasRequirements(roasReqs)
      ? buildMultiStoreRoasRows(roasReqs)
      : buildSingleStoreRoasRows(roasReqs);
  }, [localLiftMaxData]);

  // Define ROAS table columns
  const roasColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'requirement',
        headerName: lang.localLiftMaxContent.roasTableHeader,
        minWidth: 115.2,
        flex: 1,
        headerClassName: pagesStandardStyles.columnHeader,
        align: 'left',
        renderCell: params => <span style={{ fontWeight: 500 }}>{params.value}</span>,
      },
      {
        field: 'value',
        headerName: '',
        minWidth: 115.2,
        flex: 1,
        headerClassName: pagesStandardStyles.columnHeader,
        align: 'left',
        renderCell: params =>
          params.value === '-' ? (
            <span className={pagesStandardStyles.centeredPlaceholder}>{params.value}</span>
          ) : (
            params.value
          ),
      },
    ],
    [],
  );

  if (isLoading || (!isPrintView && !hasLoadedRef.current && localLiftMaxData === null)) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <SkeletonLocalLiftMax />
      </div>
    );
  }

  if (localLiftMaxData == null) {
    return isPrintView ? null : (
      <div>
        <div className={pagesStandardStyles.pageHeader}>
          <div
            className={
              pagesStandardStyles.headerContainer + ' ' + localLiftMaxStyles.headerFlexRow
            }>
            <div className={localLiftMaxStyles.headerTitleRow}>
              <div className={localLiftMaxStyles.headerTitleBlock}>
                <h2 className={pagesStandardStyles.pageTitle}>{lang.localLiftMaxContent.title}</h2>
                <p className={pagesStandardStyles.pageSubtitle}>
                  {lang.pageContent.datePrefix} N/A
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className={pagesStandardStyles.errorContent}>
          {lang.pageContent.dataUnavailableError.replace(
            '{section}',
            lang.localLiftMaxContent.title,
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={pagesStandardStyles.pageHeader}>
        <div
          className={pagesStandardStyles.headerContainer + ' ' + localLiftMaxStyles.headerFlexRow}>
          <div className={localLiftMaxStyles.headerTitleRow}>
            <div className={localLiftMaxStyles.headerTitleBlock}>
              <h2 className={pagesStandardStyles.pageTitle}>{lang.localLiftMaxContent.title}</h2>
              <p className={pagesStandardStyles.pageSubtitle}>
                {lang.pageContent.datePrefix}{' '}
                {isValidApiDate(localLiftMaxData.asOfDate)
                  ? formatLoadDate(localLiftMaxData.asOfDate)
                  : 'N/A'}
                {!isPrintView && (
                  <span className={pagesStandardStyles.viewInfoLinkWrapper}>
                    <a
                      href="#"
                      className={`${pagesStandardStyles.viewInfoLink} ${localLiftMaxStyles.viewInfoLink}`}
                      onClick={e => {
                        e.preventDefault();
                        const contentInfo = kpiContentMap['LLMAX'];
                        if (contentInfo) {
                          const ContentComponent = contentInfo.component;
                          handleAndLogClick('Local Lift Max - View Info', {
                            callback: () => {
                              setDrawerContent({
                                title: contentInfo.title || lang.pageContent.moreInformation,
                                content: <ContentComponent />,
                              });
                              setIsDrawerOpen(true);
                            },
                          });
                        }
                      }}>
                      {lang.pageContent.viewInfo}
                    </a>
                  </span>
                )}
              </p>
            </div>
          </div>
          {!isPrintView && !isMobileDevice() && (
            <div className={localLiftMaxStyles.headerMonthlyButton}>
              <MuiButton
                colors="secondary"
                iconPosition="right"
                iconType="launch"
                onClick={handleViewMonthlyPerformance}>
                {lang.localLiftMaxContent.viewMonthlyPerformance}
              </MuiButton>
            </div>
          )}
        </div>
      </div>

      {/* DataGrid Section */}
      <div className={localLiftMaxStyles.dataGridContainer}>
        <DataGrid
          rows={rowData}
          columns={columns}
          columnGroupingModel={columnGroupingModel}
          disableRowSelectionOnClick
          disableVirtualization
          disableColumnMenu
          disableColumnSorting
          disableColumnResize
          hideFooter
          autoHeight
          className={`${pagesStandardStyles.dataGrid} ${localLiftMaxStyles.mainDataGrid}`}
          getRowClassName={params =>
            getAlternatingRowClassName(
              params,
              pagesStandardStyles.evenRow,
              pagesStandardStyles.oddRow,
            )
          }
          columnHeaderHeight={52}
          columnBufferPx={1500}
          sx={{
            ...getGroupColumnHeaderStyles(lastGroupOneField),
            ...getDataGridStyles('fixedColumn'),
          }}
        />
      </div>

      {/* Investment, Email, ROAS sections — always shown (including print view) */}
      <>
        {/* Investment Text */}
        <div className={localLiftMaxStyles.investmentContainer}>
          <Typography
            variant="body1"
            className={localLiftMaxStyles.investmentText}>
            {isValidApiDate(localLiftMaxData.latestMonthlyDate)
              ? new Date(localLiftMaxData.latestMonthlyDate).getUTCFullYear()
              : new Date().getUTCFullYear()}{' '}
            {lang.localLiftMaxContent.investmentLabelSuffix} $
            {localLiftMaxData.investment != null
              ? formatNumberWithCommas(localLiftMaxData.investment.toString())
              : '-'}
          </Typography>
        </div>

        {/* Email Contact */}
        <div className={localLiftMaxStyles.emailContainer}>
          <Typography
            variant="body2"
            className={localLiftMaxStyles.emailText}>
            {lang.localLiftMaxContent.questionsEmail}{' '}
            <a
              href={`mailto:${lang.localLiftMaxContent.emailAddress}`}
              className={pagesStandardStyles.emailLink}
              onClick={() => handleAndLogClick('Local Lift Max - Email')}>
              {lang.localLiftMaxContent.emailAddress}
            </a>
          </Typography>
        </div>

        {/* Divider */}
        <div className={localLiftMaxStyles.divider}></div>

        {/* ROAS Guarantee Requirements DataGrid */}
        <div className={localLiftMaxStyles.roasGridContainer}>
          <DataGrid
            rows={roasRowData}
            columns={roasColumns}
            disableRowSelectionOnClick
            disableVirtualization
            disableColumnMenu
            disableColumnSorting
            disableColumnResize
            hideFooter
            autoHeight
            className={`${pagesStandardStyles.dataGrid} ${localLiftMaxStyles.roasGrid}`}
            getRowClassName={params =>
              getAlternatingRowClassName(
                params,
                pagesStandardStyles.evenRow,
                pagesStandardStyles.oddRow,
              )
            }
            columnHeaderHeight={52}
            columnBufferPx={1500}
            sx={{
              borderRadius: 0,
              borderLeft: '1px solid #c9c9c9',
              borderBottom: '1px solid #c9c9c9',
              '&.MuiDataGrid-root': {
                borderLeft: '1px solid #c9c9c9',
              },
              '@media (max-width: 650px)': {
                '& .MuiDataGrid-row--borderBottom': {
                  height: 'initial !important',
                },
                '& .MuiDataGrid-columnHeaders div:nth-child(2)': {
                  padding: '10px',
                  borderBottom: 'none',
                },
              },
            }}
          />
        </div>
      </>

      {/* MuiDrawer — hidden in print view */}
      {!isPrintView && (
        <MuiDrawer
          anchor="right"
          headerText={drawerContent.title}
          onClose={() => setIsDrawerOpen(false)}
          open={isDrawerOpen}>
          {drawerContent.content}
        </MuiDrawer>
      )}
    </div>
  );
};

export default LocalLiftMax;
